import { useMatch } from "@tanstack/react-router";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from "react";

/**
 * Resolved viewer mode for `/boards/{id}`. Computed by the route's
 * `beforeLoad` and threaded through `BoardSessionProvider` to every leaf
 * inside the board tree.
 */
export type BoardViewMode = "app" | "readonly-public" | "readonly-member";

/**
 * Per-leaf capability flags derived from the active `BoardViewMode`. Every
 * interactive widget on the board reads from this bag rather than from the
 * raw view mode, so we can extend the capability surface (view-only seats,
 * commenter, etc.) without touching every component.
 */
export interface BoardCapabilities {
  /** Can drag/resize blocks and persist layout. */
  canEditLayout: boolean;
  /** Can edit block content (name, columns, configs, etc.). */
  canEditBlocks: boolean;
  /** Can add/remove/edit board filters. */
  canEditFilters: boolean;
  /** Can edit board name + avatar + share settings. */
  canEditBoardMeta: boolean;
  /** Whether real-time (Ably) updates are wired for this session. */
  hasRealtime: boolean;
  /** Whether the chat panel + supervisor agent are mounted. */
  hasAI: boolean;
  /** Show the right-click block options menu and the "..." block menu. */
  showBlockOptions: boolean;
  /** Show the drag handle in the block header. */
  showDragHandle: boolean;
  /** Show the peek button on chart/stat blocks. */
  showPeek: boolean;
  /** Show the quick-search input in tables. */
  showQuickSearch: boolean;
  /** Show the block sidepane on the right of the board page. */
  showSidepane: boolean;
  /** Show the "Add block" affordance in the header + empty state. */
  showAddBlock: boolean;
}

const APP_CAPABILITIES: BoardCapabilities = {
  canEditLayout: true,
  canEditBlocks: true,
  canEditFilters: true,
  canEditBoardMeta: true,
  hasRealtime: true,
  hasAI: true,
  showBlockOptions: true,
  showDragHandle: true,
  showPeek: true,
  showQuickSearch: true,
  showSidepane: true,
  showAddBlock: true,
};

const READONLY_CAPABILITIES: BoardCapabilities = {
  canEditLayout: false,
  canEditBlocks: false,
  canEditFilters: false,
  canEditBoardMeta: false,
  hasRealtime: false,
  hasAI: false,
  showBlockOptions: false,
  showDragHandle: false,
  showPeek: false,
  showQuickSearch: false,
  showSidepane: false,
  showAddBlock: false,
};

const capabilitiesFor = (viewMode: BoardViewMode): BoardCapabilities =>
  viewMode === "app" ? APP_CAPABILITIES : READONLY_CAPABILITIES;

/** Where the board's row data is coming from for this session. */
export type BoardDataSource = "live" | "snapshot";

const sourceFor = (viewMode: BoardViewMode): BoardDataSource =>
  viewMode === "app" ? "live" : "snapshot";

/**
 * The full session bag passed through context. Static for the lifetime of
 * a route mount (viewMode is resolved in `beforeLoad`).
 */
export interface BoardSession {
  boardId: string;
  viewMode: BoardViewMode;
  source: BoardDataSource;
  capabilities: BoardCapabilities;
}

const BoardSessionContext = createContext<BoardSession | null>(null);

interface BoardSessionProviderProps {
  boardId: string;
  viewMode: BoardViewMode;
}

/**
 * Provides the active `BoardSession` to every component inside the board
 * tree. Mount once at the route level — chrome and body share the same
 * provider so a single capability bag drives the entire view.
 */
export const BoardSessionProvider = ({
  boardId,
  viewMode,
  children,
}: PropsWithChildren<BoardSessionProviderProps>) => {
  const value = useMemo<BoardSession>(
    () => ({
      boardId,
      viewMode,
      source: sourceFor(viewMode),
      capabilities: capabilitiesFor(viewMode),
    }),
    [boardId, viewMode],
  );

  return (
    <BoardSessionContext.Provider value={value}>
      {children}
    </BoardSessionContext.Provider>
  );
};

/**
 * Returns the active session.
 *
 * Falls back to the route match context when no `BoardSessionProvider` is
 * mounted above the caller — this lets cross-cutting chrome that lives
 * outside the route's `<Outlet />` (TopBar, DocumentTitle, PageTitle)
 * still read the session, since the route's `beforeLoad` writes
 * `viewMode` into the route match context on every nav.
 *
 * Returns `null` outside of any `/boards/{id}` route.
 */
export const useBoardSession = (): BoardSession | null => {
  const ctxSession = useContext(BoardSessionContext);
  const match = useMatch({ from: "/boards/$boardId", shouldThrow: false });
  if (ctxSession) return ctxSession;
  if (!match) return null;
  const viewMode = match.context?.viewMode as BoardViewMode | undefined;
  if (!viewMode) return null;
  return {
    boardId: match.params.boardId,
    viewMode,
    source: sourceFor(viewMode),
    capabilities: capabilitiesFor(viewMode),
  };
};

/**
 * Returns the active capability bag, with editor defaults when called
 * outside of a board route. Components that always live inside a board
 * tree (block chrome, board grid) get the real session; cross-cutting
 * chrome (TopBar, DocumentTitle) safely falls back to editor defaults
 * when on other routes.
 */
export const useBoardCapabilities = (): BoardCapabilities => {
  const session = useBoardSession();
  return session?.capabilities ?? APP_CAPABILITIES;
};

/** Returns the active view mode, or `null` outside of a board route. */
export const useBoardViewMode = (): BoardViewMode | null => {
  const session = useBoardSession();
  return session?.viewMode ?? null;
};
